<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { internalCall } from 'dashboard/lib/internalCallSession';
import CallControl from './CallControl.vue';

const props = defineProps({
  item: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  thumbnail: { type: Boolean, default: false },
  isNew: { type: Boolean, default: false },
});
const emit = defineEmits(['select']);
const { t } = useI18n();
const host = ref(null);
const tile = ref(null);
const fullscreen = ref(false);
let element;
const release = () => {
  if (element) internalCall.detachView(props.item.id, element);
  element = null;
};
watch(host, container => {
  release();
  if (container) element = internalCall.attachTrack(props.item.id, container);
});
const toggleFullscreen = () =>
  internalCall.run(async () => {
    const doc = tile.value.ownerDocument;
    if (doc.fullscreenElement) await doc.exitFullscreen();
    else await tile.value.requestFullscreen();
  }, 'action_failed');
const syncFullscreen = () => {
  fullscreen.value = Boolean(
    tile.value?.ownerDocument.fullscreenElement === tile.value
  );
};
onBeforeUnmount(release);
</script>

<template>
  <div
    ref="tile"
    class="relative flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border bg-n-slate-2 fullscreen:rounded-none"
    :class="
      selected && thumbnail
        ? 'border-n-blue-9 ring-2 ring-n-blue-9'
        : 'border-n-weak'
    "
    @fullscreenchange="syncFullscreen"
  >
    <div
      ref="host"
      class="absolute inset-0 [&_video]:size-full [&_video]:object-contain"
    />
    <button
      v-if="thumbnail"
      type="button"
      class="absolute inset-0 z-10 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-n-brand"
      :aria-label="
        t(
          item.source === 'screen_share'
            ? 'INTERNAL_CALL.SPOTLIGHT_SCREEN'
            : 'INTERNAL_CALL.SPOTLIGHT_CAMERA',
          { name: item.name }
        )
      "
      :aria-pressed="selected"
      @click="emit('select', item.id)"
    />
    <span
      v-if="isNew"
      class="absolute right-2 top-2 rounded-md bg-n-blue-12 px-2 py-1 text-xs font-semibold text-n-blue-1"
    >
      {{ t('INTERNAL_CALL.NEW_STREAM') }}
    </span>
    <div
      class="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2"
    >
      <span
        class="flex min-w-0 items-center gap-1.5 rounded-md bg-n-black/75 px-2 py-1 text-xs font-medium text-white"
      >
        <span
          :class="
            item.source === 'screen_share'
              ? 'i-lucide-monitor'
              : 'i-lucide-video'
          "
          class="size-3.5 shrink-0"
          aria-hidden="true"
        />
        <span class="truncate"
          >{{ item.name
          }}<span v-if="item.local"> · {{ t('INTERNAL_CALL.YOU') }}</span></span
        >
      </span>
      <CallControl
        v-if="!thumbnail"
        class="pointer-events-auto !bg-n-black/75 !text-white"
        :icon="fullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
        :label="
          t(
            fullscreen
              ? 'INTERNAL_CALL.EXIT_FULLSCREEN'
              : 'INTERNAL_CALL.FULLSCREEN'
          )
        "
        @click="toggleFullscreen"
      />
    </div>
  </div>
</template>
